import React, { useState, useMemo } from 'react';
import {
  DollarSign, UserCheck, Calendar, Briefcase, Building2, MessageSquare, Mail,
  FileText, BarChart3, ShieldCheck, Settings, Users, Sparkles, Sliders, Layers,
  ChevronDown, ChevronUp, Eye, Edit3, ShieldOff, CheckCircle2, AlertCircle, FileCheck, Package,
  UserCog, Search, Info
} from 'lucide-react';

export type PermissionLevel = 'none' | 'view' | 'edit';

export interface PermissionItem {
  key: string;
  label: string;
  desc: string;
  icon?: React.ComponentType<any>;
}

export interface ModuleGroup {
  id: string;
  name: string;
  desc: string;
  icon: React.ComponentType<any>;
  items: PermissionItem[];
}

export const MODULE_GROUPS: ModuleGroup[] = [
  {
    id: 'recruitment',
    name: 'Recruitment & Applicants',
    desc: 'Applications directory, jobs, matching, placement, program/term assignment, pipeline stages, and cancellations',
    icon: UserCheck,
    items: [
      { key: 'applicants', label: 'Applications — Directory List', desc: 'View and manage the workspace candidate roster and application records', icon: UserCheck },
      { key: 'applicant.profile', label: 'Applicant Profile — Edit Personal & Biometrics', desc: 'Modify candidate personal details, biometrics, emergency contacts, education, and work history', icon: UserCog },
      { key: 'jobs', label: 'Jobs — Postings & Openings', desc: 'View and manage host company job descriptions and active recruitment postings', icon: Briefcase },
      { key: 'applicant.matches', label: 'Applicant — Job Matches', desc: 'View, recommend, and manage candidate job interest matches', icon: Briefcase },
      { key: 'applicant.placement', label: 'Applicant — Placement & Housing', desc: 'Manage employer placements, visa details, DS-7002 terms, and housing arrangements', icon: Building2 },
      { key: 'applicant.jobs', label: 'Applicant — Assign Job Postings', desc: 'Link candidate profiles directly to open job positions', icon: Briefcase },
      { key: 'applicant.changeStage', label: 'Applicant — Pipeline Stage Changes', desc: 'Advance, reassign, or update candidate master pipeline stages', icon: Sliders },
      { key: 'applicant.cancelApplication', label: 'Applicant — Cancel or Archive Application', desc: 'Cancel, withdraw, or move candidate profiles to the archived repository', icon: AlertCircle },
      { key: 'applicant.program', label: 'Applicant — Reassign Program Offering', desc: 'Change or update the candidate assigned exchange program offering', icon: Layers },
      { key: 'applicant.term', label: 'Applicant — Reassign Cohort Term', desc: 'Update candidate enrollment term or program intake cohort', icon: Calendar },
    ],
  },
  {
    id: 'people',
    name: 'People & Partners',
    desc: 'Workspace users, host employers, referrers, sponsors, partner entities, and staff assignments',
    icon: Building2,
    items: [
      { key: 'users', label: 'Users — Staff & Team Directory', desc: 'View and manage workspace team members, account statuses, and staff details', icon: Users },
      { key: 'employers', label: 'Employers — Host Companies & Partners', desc: 'Manage partner host employer profiles, contact details, and locations', icon: Building2 },
      { key: 'referrers', label: 'Referrers — Recruiting Partners & Agencies', desc: 'Manage referring agency partners, commission rules, and sub-agents', icon: Users },
      { key: 'sponsors', label: 'Sponsors — Visa Sponsors & Organizations', desc: 'Manage visa sponsor organization profiles, requirements, and compliance specs', icon: ShieldCheck },
      { key: 'organizations', label: 'Organizations — Partner Entities', desc: 'Manage multi-tenant partner institutions and external company profiles', icon: Building2 },
      { key: 'invitations', label: 'Invitations — Send & Manage Invitations', desc: 'Issue email invitations for staff members, employer admins, and partner users', icon: Mail },
      { key: 'applicant.referrer', label: 'Applicant — Assign Partner Agency', desc: 'Assign or reassign the referring recruiter agency for candidate profiles', icon: Users },
      { key: 'applicant.teamMember', label: 'Applicant — Reassign Staff Member', desc: 'Assign or reassign internal workspace team members to candidate files', icon: UserCog },
      { key: 'applicant.partnerSponsor', label: 'Applicant — Assign Visa Sponsor', desc: 'Assign or reassign host visa sponsor entities to candidate profiles', icon: ShieldCheck },
    ],
  },
  {
    id: 'finance',
    name: 'Finance & Accounting',
    desc: 'Workspace finance accounting, candidate invoices, payment receipts, and payment allocations',
    icon: DollarSign,
    items: [
      { key: 'finance', label: 'Finance — Workspace Accounting & Billing', desc: 'Master financial dashboard, revenue analytics, master invoices, and product catalog', icon: DollarSign },
      { key: 'applicant.invoice', label: 'Applicant — Invoices & Billing', desc: 'Create, issue, view, and send billing invoices for candidate profiles', icon: FileText },
      { key: 'applicant.payments', label: 'Applicant — Payments & Receipts', desc: 'Record, review, approve, and view payment receipts for candidate files', icon: DollarSign },
      { key: 'applicant.paymentAllocation', label: 'Applicant — Payment Allocations & Refunds', desc: 'Apply payment credits across invoices and process payment refund requests', icon: DollarSign },
    ],
  },
  {
    id: 'documents',
    name: 'Documents & Compliance',
    desc: 'Document templates, document checklists, generation, legal packages, and e-sign requests',
    icon: FileText,
    items: [
      { key: 'doc-templates', label: 'Document Templates — Contracts & Forms', desc: 'Create and manage contract, DS-2019, agreement, and legal document templates', icon: FileText },
      { key: 'applicant.documents', label: 'Applicant — Document Checklists & Uploads', desc: 'View, upload, review, and approve candidate document checklist items', icon: FileText },
      { key: 'applicant.generateDocs', label: 'Applicant — Generate Contract & DS-2019 PDFs', desc: 'Generate dynamic contracts, agreements, and official PDF documents for candidate files', icon: FileText },
      { key: 'applicant.packages', label: 'Applicant — Program Packages & Downloads', desc: 'Manage and issue program welcome packages and legal document downloads', icon: Package },
      { key: 'applicant.signRequest', label: 'Applicant — Digital E-Signature Requests', desc: 'Send, track, and manage digital contract signature requests', icon: FileCheck },
      { key: 'applicant.specialRequest', label: 'Applicant — Special Form & Document Requests', desc: 'Issue custom form requests, upload requirements, and special candidate requests', icon: FileCheck },
    ],
  },
  {
    id: 'communications',
    name: 'Communications & Scheduling',
    desc: 'Events, personal schedules, email templates, direct candidate emails, and support tickets',
    icon: Calendar,
    items: [
      { key: 'my-events', label: 'My Events — Personal Schedule & Bookings', desc: 'Access personal calendar, assigned interviews, and individual event bookings', icon: Calendar },
      { key: 'events', label: 'Workspace Events — Events & Group Calendar', desc: 'Manage workspace-wide event calendars, interview slots, and group bookings', icon: Calendar },
      { key: 'communications', label: 'Communications — Messaging & Notifications', desc: 'Overview of candidate message channels, chat activity, and notifications', icon: MessageSquare },
      { key: 'applicant.sendEmail', label: 'Applicant — Send Direct Email', desc: 'Compose direct emails to candidates and inspect dispatch communication logs', icon: Mail },
      { key: 'applicant.events', label: 'Applicant — Interview & Event Bookings', desc: 'Schedule candidate interview slots, orientations, and calendar appointments', icon: Calendar },
      { key: 'templates', label: 'Email Templates — Broadcast & Canned Templates', desc: 'Manage email marketing templates, canned responses, and broadcast designs', icon: Mail },
    ],
  },
  {
    id: 'operations',
    name: 'Operations & Oversight',
    desc: 'Batch center, reports, approvals, tickets, AI suggestions, screening, activity logs, and PII access',
    icon: BarChart3,
    items: [
      { key: 'batch', label: 'Batches — Cohort Operations & Bulk Broadcasts', desc: 'Manage applicant cohorts, bulk finance runs, and batch email campaigns', icon: Sliders },
      { key: 'reports', label: 'Reports — Analytics & Operational Reports', desc: 'Access operational performance metrics, financial breakdowns, and recruitment reports', icon: BarChart3 },
      { key: 'approvals', label: 'Approvals — Partner & Document Queues', desc: 'Review and process pending employer, document, and partner approval queues', icon: ShieldCheck },
      { key: 'tickets', label: 'Tickets — Support Desk & Messaging', desc: 'Access and resolve workspace support tickets and partner inquiries', icon: MessageSquare },
      { key: 'applicant.tickets', label: 'Applicant — Support Tickets', desc: 'Handle inbound candidate support tickets and support communications', icon: MessageSquare },
      { key: 'ai-suggestions', label: 'AI Suggestions — Copilot Risk Audit', desc: 'View and act on AI copilot risk audit recommendations and profile flags', icon: Sparkles },
      { key: 'applicant.screening', label: 'Applicant — Screening & Copilot Reviews', desc: 'Conduct candidate screening, manage copilot evaluation notes, and review rubrics', icon: Sparkles },
      { key: 'applicant.activity', label: 'Applicant — Activity Timeline Logs', desc: 'Access candidate history timeline, system audit logs, and status transitions', icon: Sliders },
      { key: 'applicant.comments', label: 'Applicant — Staff Timeline Comments', desc: 'View and post internal staff-only comments on candidate profile timelines', icon: MessageSquare },
      { key: 'applicant.notes', label: 'Applicant — Staff Profile Notes', desc: 'View and edit internal staff-only notes block on candidate profiles', icon: FileText },
      { key: 'applicant.showContact', label: 'Applicant — Unmasked Contact Details (PII)', desc: 'View unmasked email addresses, phone numbers, street addresses, and passport details', icon: Eye },
    ],
  },
];

// Helper to create empty "No Access" permissions map
export const createDefaultNoAccessPermissions = (): Record<string, PermissionLevel> => {
  const perms: Record<string, PermissionLevel> = {};
  MODULE_GROUPS.forEach(group => {
    group.items.forEach(item => {
      perms[item.key] = 'none';
    });
  });
  perms.applicantsScope = 'all' as any;
  return perms;
};

interface Props {
  roleName?: string;
  permissions: Record<string, string>;
  onChange: (updatedPermissions: Record<string, string>) => void;
  disabled?: boolean;
}

export default function CustomRolePermissionsEditor({ roleName, permissions, onChange, disabled }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    MODULE_GROUPS.forEach(g => { init[g.id] = true; }); // Start expanded for clear configuration overview
    return init;
  });

  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const getGroupLevel = (group: ModuleGroup): PermissionLevel | 'mixed' => {
    const levels = group.items.map(item => permissions[item.key] || 'none');
    const first = levels[0];
    const allSame = levels.every(l => l === first);
    return allSame ? (first as PermissionLevel) : 'mixed';
  };

  const setGroupLevel = (group: ModuleGroup, level: PermissionLevel) => {
    if (disabled) return;
    const next = { ...permissions };
    group.items.forEach(item => {
      next[item.key] = level;
    });
    onChange(next);
  };

  const setItemLevel = (key: string, level: PermissionLevel) => {
    if (disabled) return;
    onChange({ ...permissions, [key]: level });
  };

  const setApplicantsScope = (scope: 'all' | 'assigned') => {
    if (disabled) return;
    onChange({ ...permissions, applicantsScope: scope });
  };

  // Filtered module groups based on search query
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return MODULE_GROUPS;

    return MODULE_GROUPS.map(group => {
      const groupMatch = group.name.toLowerCase().includes(q) || group.desc.toLowerCase().includes(q);
      const matchingItems = group.items.filter(item =>
        groupMatch ||
        item.label.toLowerCase().includes(q) ||
        item.key.toLowerCase().includes(q) ||
        item.desc.toLowerCase().includes(q)
      );

      return {
        ...group,
        items: matchingItems,
      };
    }).filter(group => group.items.length > 0);
  }, [searchQuery]);

  // Summary Metrics
  const summary = useMemo(() => {
    let total = 0;
    let viewOnly = 0;
    let viewAndEdit = 0;
    let noAccess = 0;

    MODULE_GROUPS.forEach(group => {
      group.items.forEach(item => {
        total++;
        const lvl = permissions[item.key] || 'none';
        if (lvl === 'edit') viewAndEdit++;
        else if (lvl === 'view') viewOnly++;
        else noAccess++;
      });
    });

    return { total, viewOnly, viewAndEdit, noAccess, accessible: viewOnly + viewAndEdit };
  }, [permissions]);

  return (
    <div className="space-y-4">
      {/* Top Permission Summary Banner aligned with Workspace Branding */}
      <div className="bg-white text-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-slate-200">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Custom Role Configuration</div>
            <div className="text-base font-black text-slate-900">{roleName || 'Custom Role'}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-brand/20 text-brand border border-brand/30 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-brand" />
              {summary.accessible} / {summary.total} Accessible Capabilities
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <div className="bg-slate-50 rounded-xl p-2 border border-slate-200">
            <span className="block text-[10px] font-bold text-slate-500 uppercase">View & Edit Access</span>
            <span className="text-sm font-black text-indigo-600">{summary.viewAndEdit}</span>
          </div>
          <div className="bg-slate-50 rounded-xl p-2 border border-slate-200">
            <span className="block text-[10px] font-bold text-slate-500 uppercase">View Only</span>
            <span className="text-sm font-black text-emerald-600">{summary.viewOnly}</span>
          </div>
          <div className="bg-slate-50 rounded-xl p-2 border border-slate-200">
            <span className="block text-[10px] font-bold text-slate-500 uppercase">No Access (Hidden)</span>
            <span className="text-sm font-black text-rose-600">{summary.noAccess}</span>
          </div>
          <div className="bg-slate-50 rounded-xl p-2 border border-slate-200">
            <span className="block text-[10px] font-bold text-slate-500 uppercase">Module Categories</span>
            <span className="text-sm font-black text-slate-700">{MODULE_GROUPS.length}</span>
          </div>
        </div>
      </div>

      {/* Access Level Guide Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 p-3 rounded-2xl bg-slate-50 border border-slate-200 text-xs">
        <div className="flex items-start gap-2">
          <ShieldOff className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <span className="font-extrabold text-slate-800 block text-[11px]">No Access (Hidden)</span>
            <span className="text-[11px] text-slate-500">Feature is completely hidden from navigation and direct access is denied.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Eye className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-extrabold text-slate-800 block text-[11px]">View Only</span>
            <span className="text-[11px] text-slate-500">Resource is readable; creation, editing, and deletion controls are hidden/disabled.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Edit3 className="h-4 w-4 text-indigo-600 shrink-0 mt-0.5" />
          <div>
            <span className="font-extrabold text-slate-800 block text-[11px]">View & Edit Access</span>
            <span className="text-[11px] text-slate-500">Full access granted to view, create, edit, update, and manage the resource.</span>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Filter permissions by keyword (e.g., Finance, Documents, Edit, Email)..."
          className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 bg-white text-xs font-medium placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand shadow-2xs"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600"
          >
            Clear
          </button>
        )}
      </div>

      {/* Module Groups List */}
      <div className="space-y-3">
        {filteredGroups.map(group => {
          const groupLevel = getGroupLevel(group);
          const isExpanded = !!expandedGroups[group.id] || !!searchQuery.trim();
          const GroupIcon = group.icon;

          return (
            <div key={group.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-2xs transition-all">
              {/* Group Header Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-50/90 border-b border-slate-100">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-8 w-8 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                    <GroupIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs font-black text-slate-800 truncate">{group.name}</h4>
                    <p className="text-[11px] text-slate-500 truncate">{group.desc}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Group Level Quick Selector */}
                  <div className="flex items-center bg-white rounded-xl border border-slate-200 p-0.5 shadow-2xs">
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setGroupLevel(group, 'none')}
                      className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                        groupLevel === 'none'
                          ? 'bg-rose-500 text-white shadow-xs'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title="Set all capabilities in this group to No Access"
                    >
                      <ShieldOff className="h-3 w-3" />
                      No Access
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setGroupLevel(group, 'view')}
                      className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                        groupLevel === 'view'
                          ? 'bg-emerald-500 text-white shadow-xs'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title="Set all capabilities in this group to View Only"
                    >
                      <Eye className="h-3 w-3" />
                      View Only
                    </button>
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => setGroupLevel(group, 'edit')}
                      className={`px-2 py-1 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 cursor-pointer ${
                        groupLevel === 'edit'
                          ? 'bg-brand-primary text-white shadow-xs'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title="Set all capabilities in this group to View & Edit"
                    >
                      <Edit3 className="h-3 w-3" />
                      View & Edit
                    </button>
                  </div>

                  {/* Expand / Collapse Toggle */}
                  <button
                    type="button"
                    onClick={() => toggleGroupExpand(group.id)}
                    className="p-1.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-100 cursor-pointer"
                    title={isExpanded ? 'Collapse group' : 'Expand group'}
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Group Items Grid */}
              {isExpanded && (
                <div className="p-3 space-y-2 bg-white">
                  <div className="grid grid-cols-1 gap-2.5">
                    {group.items.map(item => {
                      const itemLevel = permissions[item.key] || 'none';
                      const ItemIcon = item.icon || GroupIcon;

                      return (
                        <div
                          key={item.key}
                          className={`p-3 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                            itemLevel === 'edit'
                              ? 'border-indigo-200 bg-indigo-50/20'
                              : itemLevel === 'view'
                              ? 'border-emerald-200 bg-emerald-50/20'
                              : 'border-slate-100 bg-slate-50/50'
                          }`}
                        >
                          {/* Left Title & Description */}
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`p-2 rounded-xl shrink-0 mt-0.5 ${
                              itemLevel === 'edit' ? 'bg-indigo-100 text-indigo-700' :
                              itemLevel === 'view' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-slate-100 text-slate-400'
                            }`}>
                              <ItemIcon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                <span className="text-xs font-black text-slate-900 leading-snug">{item.label}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider ${
                                  itemLevel === 'edit' ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' :
                                  itemLevel === 'view' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                                  'bg-rose-100 text-rose-800 border border-rose-200'
                                }`}>
                                  {itemLevel === 'edit' ? 'View & Edit' : itemLevel === 'view' ? 'View Only' : 'No Access'}
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 leading-normal">{item.desc}</p>
                            </div>
                          </div>

                          {/* Right Segmented Control */}
                          <div className="flex items-center bg-slate-100 p-0.5 rounded-xl border border-slate-200 shrink-0 self-start sm:self-center">
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => setItemLevel(item.key, 'none')}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                                itemLevel === 'none'
                                  ? 'bg-rose-500 text-white shadow-xs font-black'
                                  : 'text-slate-600 hover:text-slate-900'
                              }`}
                              title={`Set ${item.label} to No Access`}
                              aria-label={`Set ${item.label} to No Access`}
                            >
                              <ShieldOff className="h-3 w-3" />
                              No Access
                            </button>
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => setItemLevel(item.key, 'view')}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                                itemLevel === 'view'
                                  ? 'bg-success text-white shadow-xs font-black'
                                  : 'text-slate-600 hover:text-slate-900'
                              }`}
                              title={`Set ${item.label} to View Only`}
                              aria-label={`Set ${item.label} to View Only`}
                            >
                              <Eye className="h-3 w-3" />
                              View Only
                            </button>
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => setItemLevel(item.key, 'edit')}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer flex items-center gap-1 ${
                                itemLevel === 'edit'
                                  ? 'bg-brand-primary text-white shadow-xs font-black'
                                  : 'text-slate-600 hover:text-slate-900'
                              }`}
                              title={`Set ${item.label} to View & Edit`}
                              aria-label={`Set ${item.label} to View & Edit`}
                            >
                              <Edit3 className="h-3 w-3" />
                              View & Edit
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Special Applicant Scope Toggle if Recruitment group */}
                  {group.id === 'recruitment' && (
                    <div className="mt-2.5 p-3 bg-slate-50 rounded-xl border border-slate-200 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <span className="text-xs font-bold text-slate-800">Applicant Record Visibility Scope (Axis B)</span>
                        <p className="text-[11px] text-slate-500">Determine whether this role can view all applicants in the workspace or only assigned candidates</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => setApplicantsScope('all')}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            (permissions.applicantsScope || 'all') === 'all'
                              ? 'bg-brand text-white font-bold'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          All Applicants
                        </button>
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => setApplicantsScope('assigned')}
                          className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            permissions.applicantsScope === 'assigned'
                              ? 'bg-brand text-white font-bold'
                              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          Assigned Only
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filteredGroups.length === 0 && (
          <div className="p-8 text-center text-slate-500 bg-white rounded-2xl border border-slate-200 space-y-2">
            <Info className="h-6 w-6 text-slate-400 mx-auto" />
            <p className="text-xs font-bold">No permission capabilities match &quot;{searchQuery}&quot;.</p>
            <p className="text-[11px] text-slate-500">Try searching for a different keyword or clear the search filter.</p>
          </div>
        )}
      </div>
    </div>
  );
}
